import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useApi, getErrorMessage } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  Activity, RefreshCw, Building2, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, Wrench, ChevronRight, Sparkles,
  Shield, Droplets, Sofa, ThumbsUp, Zap, Search
} from 'lucide-react';
import { PropertyDetailModal } from './PropertyDetailModal';

const SCORE_LABELS = {
  fluidite: { label: 'Fluidité', icon: Zap, description: 'Check-in, communication, process' },
  equipement: { label: 'Équipement', icon: Wrench, description: 'Appareils, outils, fonctionnalité' },
  proprete: { label: 'Propreté', icon: Droplets, description: 'Hygiène, entretien' },
  confort: { label: 'Confort', icon: Sofa, description: 'Literie, ambiance, commodités' },
  satisfaction: { label: 'Satisfaction', icon: ThumbsUp, description: 'Expérience globale' },
};

const getScoreColor = (score) => {
  if (score >= 4) return { bg: 'bg-emerald-50', text: 'text-emerald-600', bar: '#10b981' };
  if (score >= 3) return { bg: 'bg-amber-50', text: 'text-amber-600', bar: '#f59e0b' };
  return { bg: 'bg-red-50', text: 'text-red-600', bar: '#ef4444' };
};

const ScoreBar = ({ label, score, icon: Icon }) => {
  const colors = getScoreColor(score);
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon size={14} className={colors.text} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between mb-1">
          <span className="text-xs font-medium text-slate-600">{label}</span>
          <span className={`text-xs font-bold ${colors.text}`}>{score}/5</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(score / 5) * 100}%`, backgroundColor: colors.bar }}
          />
        </div>
      </div>
    </div>
  );
};

export const QualityInsightsPage = () => {
  const api = useApi();
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [scoresRes, schedulerRes] = await Promise.all([
        api.get('/quality/property-scores'),
        api.get('/quality/scheduler-status').catch(() => ({ data: {} }))
      ]);
      // Endpoint now returns {properties, summary} with the headline numbers
      // pre-computed server-side. Stay backward-compatible with the old flat
      // array shape so no deploy ordering issue can blank the page out.
      const data = scoresRes.data;
      if (Array.isArray(data)) {
        setProperties(data);
        setSummary(null);
      } else {
        setProperties(data.properties || []);
        setSummary(data.summary || null);
      }
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du chargement des scores'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await api.post('/quality/analyze-conversations');
      toast.success('Analyse des conversations lancée en arrière-plan. Actualisez dans quelques minutes.');
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du lancement de l'analyse"));
    } finally {
      setAnalyzing(false);
    }
  };

  const filtered = properties.filter(p =>
    p.property_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // The backend returns these counters pre-computed. Fall back to a local
  // computation when the response shape is the older flat array (during the
  // brief window between deploys).
  const totalProperties = summary?.total_properties ?? properties.length;
  const analyzedCount = summary?.analyzed_properties
    ?? properties.filter(p => p.has_data !== false && p.scores).length;
  const totalIssues = summary?.total_open_issues
    ?? properties.reduce((s, p) => s + (p.open_issues || 0), 0);
  const totalAnalyzed = summary?.total_bookings_analyzed
    ?? properties.reduce((s, p) => s + (p.bookings_analyzed || 0), 0);

  const avgScores = summary?.avg_scores ?? (() => {
    const a = properties.filter(p => p.has_data !== false && p.scores);
    if (a.length === 0) return null;
    return {
      fluidite: +(a.reduce((s, p) => s + p.scores.fluidite, 0) / a.length).toFixed(1),
      equipement: +(a.reduce((s, p) => s + p.scores.equipement, 0) / a.length).toFixed(1),
      proprete: +(a.reduce((s, p) => s + p.scores.proprete, 0) / a.length).toFixed(1),
      confort: +(a.reduce((s, p) => s + p.scores.confort, 0) / a.length).toFixed(1),
      satisfaction: +(a.reduce((s, p) => s + p.scores.satisfaction, 0) / a.length).toFixed(1),
      global: +(a.reduce((s, p) => s + p.scores.global, 0) / a.length).toFixed(1),
    };
  })();

  const radarData = avgScores ? Object.entries(SCORE_LABELS).map(([key, { label }]) => ({
    dimension: label,
    score: avgScores[key],
    fullMark: 5
  })) : [];

  if (loading) {
    return (
      <MainLayout>
        <Header title="Quality Insights" />
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Header title="Quality Insights" subtitle="Scores qualité et axes d'amélioration par propriété" />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="quality-insights-page">
        {/* Empty state */}
        {properties.length === 0 ? (
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#FFF0F1] flex items-center justify-center mx-auto mb-4">
                <Activity className="h-8 w-8 text-[#FF5A6E]" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune analyse disponible</h3>
              <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                Lancez une analyse des conversations Beds24 pour obtenir des scores de qualité
                et des axes d'amélioration pour chacune de vos propriétés.
              </p>
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
                data-testid="start-analysis-btn"
              >
                <Sparkles size={16} className="mr-2" />
                {analyzing ? 'Lancement...' : 'Lancer l\'analyse des conversations'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#FFF0F1] flex items-center justify-center">
                    <Activity className="h-5 w-5 text-[#FF5A6E]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{avgScores?.global || '-'}<span className="text-sm font-normal text-slate-400">/5</span></p>
                    <p className="text-xs text-slate-500">Score global</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{analyzedCount}<span className="text-sm font-normal text-slate-400">/{totalProperties}</span></p>
                    <p className="text-xs text-slate-500">Propriétés analysées</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{totalAnalyzed}</p>
                    <p className="text-xs text-slate-500">Séjours analysés</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{totalIssues}</p>
                    <p className="text-xs text-slate-500">Problèmes ouverts</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Automation status */}
            <Card className="bg-slate-50 border-slate-100">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-6 text-xs text-slate-500">
                  <div className="flex items-center gap-2" data-testid="scheduler-daily-sync">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span><span className="font-medium text-slate-700">Sync Beds24</span> — quotidien (06h00)</span>
                    {schedulerStatus?.daily_sync?.last_run && (
                      <span className="text-slate-400">
                        dernier : {new Date(schedulerStatus.daily_sync.last_run).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2" data-testid="scheduler-daily-analysis">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span><span className="font-medium text-slate-700">Analyse IA</span> — quotidien (08h00)</span>
                    {schedulerStatus?.monthly_analysis?.last_run && (
                      <span className="text-slate-400">
                        dernier : {new Date(schedulerStatus.monthly_analysis.last_run).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleAnalyze} disabled={analyzing} data-testid="manual-analysis-btn">
                  <RefreshCw size={14} className={`mr-2 ${analyzing ? 'animate-spin' : ''}`} />
                  {analyzing ? 'Analyse...' : 'Lancer manuellement'}
                </Button>
              </CardContent>
            </Card>

            {/* Global radar + top/bottom properties */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Radar chart */}
              <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Shield size={14} className="text-[#FF5A6E]" />
                    Score moyen global
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-2">
                  <ResponsiveContainer width="100%" height={220}>
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 9, fill: '#94a3b8' }} />
                      <Radar name="Score" dataKey="score" stroke="#FF5A6E" fill="#FF5A6E" fillOpacity={0.15} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Score bars + actions */}
              <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Scores par dimension</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {avgScores && Object.entries(SCORE_LABELS).map(([key, { label, icon }]) => (
                      <ScoreBar key={key} label={label} score={avgScores[key]} icon={icon} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Property ranking */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Building2 size={14} className="text-slate-500" />
                  Classement des propriétés
                </CardTitle>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#FF5A6E]"
                    data-testid="property-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {filtered.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400">Aucune propriété trouvée</div>
                  ) : (
                    filtered.map((prop, idx) => {
                      const hasData = prop.has_data !== false && prop.scores;
                      const scoreColor = hasData ? getScoreColor(prop.scores.global) : { bg: 'bg-slate-50', text: 'text-slate-400', bar: '#cbd5e1' };
                      return (
                        <button
                          key={prop.property_id}
                          onClick={() => hasData ? setSelectedProperty(prop) : null}
                          className={`w-full flex items-center gap-4 p-4 transition-colors text-left ${hasData ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-70 cursor-default'}`}
                          data-testid={`property-row-${prop.property_id}`}
                        >
                          <span className="w-6 text-xs font-bold text-slate-300 text-center">#{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{prop.property_name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              {hasData ? (
                                <span className="text-xs text-slate-400">{prop.bookings_analyzed} séjours</span>
                              ) : (
                                <span className="text-xs text-slate-400 italic">En attente d'analyse</span>
                              )}
                              {prop.open_issues > 0 && (
                                <span className="text-xs text-red-500 flex items-center gap-1">
                                  <AlertTriangle size={10} /> {prop.open_issues} problème{prop.open_issues > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Mini score bars */}
                          {hasData && (
                            <div className="hidden md:flex items-center gap-1.5">
                              {Object.entries(SCORE_LABELS).map(([key]) => {
                                const s = prop.scores[key];
                                const c = getScoreColor(s);
                                return (
                                  <div key={key} className="flex flex-col items-center gap-0.5" title={SCORE_LABELS[key].label}>
                                    <div className="w-8 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full" style={{ width: `${(s / 5) * 100}%`, backgroundColor: c.bar }} />
                                    </div>
                                    <span className="text-[9px] text-slate-400">{s}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Trend indicator */}
                          {prop.trend && (
                            <div className={`flex flex-col items-center justify-center w-8 ${
                              prop.trend === 'up' ? 'text-emerald-500' : prop.trend === 'down' ? 'text-red-500' : 'text-slate-400'
                            }`} title={prop.trend_value > 0 ? `+${prop.trend_value}` : `${prop.trend_value}`}>
                              {prop.trend === 'up' && <TrendingUp size={16} />}
                              {prop.trend === 'down' && <TrendingDown size={16} />}
                              {prop.trend === 'stable' && <span className="text-xs font-medium">=</span>}
                              {prop.trend !== 'stable' && (
                                <span className="text-[9px] font-bold">{prop.trend_value > 0 ? '+' : ''}{prop.trend_value}</span>
                              )}
                            </div>
                          )}

                          <div className={`w-12 h-12 rounded-xl ${scoreColor.bg} flex flex-col items-center justify-center`}>
                            {hasData ? (
                              <>
                                <span className={`text-lg font-bold ${scoreColor.text}`}>{prop.scores.global}</span>
                                <span className="text-[8px] text-slate-400">/5</span>
                              </>
                            ) : (
                              <span className="text-xs font-medium text-slate-400">N/A</span>
                            )}
                          </div>
                          {hasData ? <ChevronRight size={16} className="text-slate-300" /> : <div className="w-4" />}
                        </button>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Property detail modal */}
      {selectedProperty && (
        <PropertyDetailModal
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
        />
      )}
    </MainLayout>
  );
};

export default QualityInsightsPage;
